import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import CrashHistoryList from "src/components/ServerMonitoring/CrashHistoryList";
import CrashIncidentDetailModal from "src/components/ServerMonitoring/CrashIncidentDetailModal";
// import DeploymentHistory from "src/components/ServerMonitoring/DeploymentHistory";
import DatabaseHealth from "src/components/ServerMonitoring/DatabaseHealth";
import DomainHealth from "src/components/ServerMonitoring/DomainHealth";
import RepeatedCrashes from "src/components/ServerMonitoring/RepeatedCrashes";
// import RecentLogs from "src/components/ServerMonitoring/RecentLogs";
// import ResourceTrends from "src/components/ServerMonitoring/ResourceTrends";
import ServerStatusOverview from "src/components/ServerMonitoring/ServerStatusOverview";

const STATUS_REFRESH_MS = 30 * 1000;
const STATUS_REFRESH_DEPLOYING_MS = 10 * 1000; // follow a deployment (restart / redeploy / rollback) more closely

// "Server Monitoring" tab of Site Settings (Super Admins only).
const ServerMonitoringPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [checking, setChecking] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const selectedIncidentId = searchParams.get("incident");

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiRequest("get", `/server-monitor/status`);
      setStatus(res.data.data);
    } catch (error) {
    } finally {
      setStatusLoaded(true);
    }
  }, []);

  // The status endpoint only reads MongoDB (the backend job talks to DigitalOcean), so polling it is cheap.
  const deploying = !!status?.app?.in_progress_deployment;
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  useEffect(() => {
    const interval = setInterval(loadStatus, deploying ? STATUS_REFRESH_DEPLOYING_MS : STATUS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadStatus, deploying]);

  const refreshAll = () => {
    loadStatus();
    setHistoryRefreshKey((key) => key + 1);
  };

  const checkNow = async () => {
    try {
      setChecking(true);
      const res = await apiRequest("post", `/server-monitor/check-now`, { body: {} });
      if (res.data.data) setStatus(res.data.data);
      toast.success(res.data.message || "SaaS Portal status refreshed.");
      setHistoryRefreshKey((key) => key + 1);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChecking(false);
    }
  };

  // Keep the other query params (e.g. the selected settings tab) when opening/closing an incident.
  const updateIncidentParam = (incidentId) => {
    const params = new URLSearchParams(searchParams);
    if (incidentId) params.set("incident", incidentId);
    else params.delete("incident");
    setSearchParams(params);
  };

  return (
    <div className="pb-6">
      <ServerStatusOverview
        status={status}
        loaded={statusLoaded}
        checking={checking}
        onCheckNow={checkNow}
        onChanged={refreshAll}
      />
      <div className="mt-6 space-y-6">
        {/* <ResourceTrends status={status} /> */}
        {/* <DeploymentHistory status={status} /> */}
        <DatabaseHealth status={status} />
        <DomainHealth status={status} />
        {/* <RecentLogs status={status} /> */}
      </div>
      <RepeatedCrashes
        groups={status?.repeated_crashes || []}
        onView={(incident) => updateIncidentParam(incident._id)}
      />
      {status?.configured !== false && (
        <CrashHistoryList
          refreshKey={historyRefreshKey}
          onView={(incident) => updateIncidentParam(incident._id)}
        />
      )}
      <CrashIncidentDetailModal
        incidentId={selectedIncidentId}
        onClose={() => updateIncidentParam(null)}
        onChanged={refreshAll}
      />
    </div>
  );
};

export default ServerMonitoringPanel;
